import React, {useEffect,useState} from 'react';
import {Alert,Button,Form,InputGroup,Modal} from 'react-bootstrap';
import {Link} from 'react-router-dom';
import {Search} from 'lucide-react';
import {registerEvidence,searchEvidence} from '../../../../services/ReconciliationActionService';

const BLOB_UUID_PREFIX=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;
export const evidenceName=doc=>String(doc?.blob_name||'').split('/').pop().replace(BLOB_UUID_PREFIX,'');

const small={fontSize:'0.8125rem'};
const help={fontSize:'0.75rem',color:'#6b7280'};
const OWNERS={
    verification:{text:'ett verifikat',href:(company,id)=>`/${company}/edit-verification/${id}`},
    supplier_invoice:{text:'en leverantörsfaktura',href:(company,id)=>`/${company}/supplier-invoices/${id}`},
    customer_invoice:{text:'en kundfaktura',href:(company,id)=>`/${company}/view-invoice/${id}`},
};

function Usage({companyId,doc}) {
    if(doc.conflict)return <span>{doc.conflict.message}</span>;
    if(doc.unregistered||!doc.owner)return <span>Inte bokfört</span>;
    const owner=OWNERS[doc.owner.owner_type];
    if(!owner)return <span>Används redan i bokföringen</span>;
    return <span>Används av <Link to={owner.href(companyId,doc.owner.owner_id)} target="_blank" rel="noreferrer" style={{textDecoration:'none'}}>{owner.text}</Link></span>;
}

/**
 * The company's files (Dokumentarkivet) with a search, without any frame: a
 * modal that already is one (Koppla till befintligt verifikat) shows it in
 * its own body instead of stacking a second modal on top. A file that has
 * never been used as underlag is registered when it is picked.
 */
export function EvidenceList({companyId,active=true,onPick}) {
    const [q,setQ]=useState(''),[page,setPage]=useState(null),[error,setError]=useState(''),[loading,setLoading]=useState(false),[picking,setPicking]=useState(null);
    const load=async cursor=>{
        setLoading(true);
        try{setPage(await searchEvidence(companyId,{q,cursor}));setError('');}
        catch(e){setError(e.response?.data?.message || 'Kunde inte hämta underlag.');}
        finally{setLoading(false);}
    };
    const pick=async d=>{
        if(!d.unregistered)return onPick(d);
        setPicking(d.blob_name);
        try{onPick(await registerEvidence(companyId,d.blob_name));}
        catch(e){setError(e.response?.data?.message || 'Filen kunde inte användas som underlag.');}
        finally{setPicking(null);}
    };
    useEffect(()=>{if(active)load();/* eslint-disable-next-line react-hooks/exhaustive-deps */},[active,companyId]);
    return <div style={small}>
        <Form onSubmit={e=>{e.preventDefault();load();}}>
            <InputGroup size="sm">
                <Form.Control type="search" aria-label="Sök underlag" placeholder="Sök filnamn eller beskrivning" value={q} onChange={e=>setQ(e.target.value)}/>
                <Button type="submit" variant="outline-secondary" disabled={loading} aria-label="Sök"><Search size={14}/></Button>
            </InputGroup>
        </Form>
        {error && <Alert variant="warning" className="py-2 mt-2 mb-0" style={small}>{error}</Alert>}
        {page?.items.map(d=><div className="d-flex align-items-center gap-2 border-bottom py-2" key={d.document_id}>
            <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:500,color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={evidenceName(d)}>{evidenceName(d)}</div>
                <div style={help}><Usage companyId={companyId} doc={d}/></div>
            </div>
            <Button size="sm" variant="outline-primary" style={{flexShrink:0}} disabled={!(d.identity_ready||d.unregistered)||picking!==null} onClick={()=>pick(d)}>{picking===d.blob_name?'Väljer…':'Välj'}</Button>
        </div>)}
        {page && !page.items.length && !error && <div style={{...help,marginTop:10}}>Inga filer hittades. Ladda upp kvittot i Dokumentarkivet så kan du välja det här.</div>}
        {page?.truncated && <Button variant="link" size="sm" className="p-0 mt-2" style={{...small,textDecoration:'none'}} disabled={loading} onClick={()=>load(page.cursor)}>Visa fler</Button>}
    </div>;
}

export default function EvidencePicker({companyId,show,onHide,onPick}) {
    return <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton><Modal.Title as="h6">Välj underlag</Modal.Title></Modal.Header>
        <Modal.Body><EvidenceList companyId={companyId} active={show} onPick={d=>{onPick(d);onHide();}}/></Modal.Body>
    </Modal>;
}
